import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  Separator,
} from '@design-system';
import { CheckoutLayout, useStartTrialStep } from '@/features/advisor-onboarding';
import { SubscriptionStatusContent } from '@client/components/advisor-account-seats';

import { Check, Clock, TrendingUp, Shield, Sparkles, Loader2, LucideIcon } from 'lucide-react';

import { useNavigate } from 'react-router-dom';

const FEATURES = [
  {
    icon: Clock,
    title: 'Create LTC plans in minutes',
    description: 'Start impressing your clients today',
  },
  {
    icon: TrendingUp,
    title: 'Analyze any type of LTC product',
    description: 'Plus instantly quote from leading carriers',
  },
  {
    icon: Shield,
    title: 'HIPAA compliant & SOC 2 certified',
    description: 'Enterprise-grade security for your clients',
  },
] as const;

const TRIAL_BENEFITS = [
  'Create unlimited LTC plans for up to 100 clients',
  'Full access to AI-powered recommendations',
  'Live customer support',
] as const;

const PAID_PLANS = [
  {
    id: 'annual' as const,
    title: 'Annual',
    price: '$47',
    billingNote: 'Billed annually',
    benefits: ['All trial features', 'Best value'],
    badge: 'SAVE 20%',
  },
  {
    id: 'monthly' as const,
    title: 'Monthly',
    price: '$59',
    billingNote: 'No commitment',
    benefits: ['All trial features', 'Cancel anytime'],
    badge: null,
  },
] as const;

interface FeatureItemProps {
  icon: LucideIcon;
  title: string;
  description: string;
}

function FeatureItem({ icon: Icon, title, description }: FeatureItemProps) {
  return (
    <Item className="border-border shadow-xs items-center rounded-xl border p-4">
      <ItemMedia className="bg-primary/10 translate-y-0! flex size-12 shrink-0 items-center justify-center rounded-full">
        <Icon className="text-primary size-5" />
      </ItemMedia>
      <ItemContent>
        <ItemTitle className="text-sm font-semibold text-gray-950 sm:text-base">{title}</ItemTitle>
        <ItemDescription className="text-foreground text-xs sm:text-sm">
          {description}
        </ItemDescription>
      </ItemContent>
    </Item>
  );
}

interface AdvisorOnboardingStartTrialStepProps {
  advisorId: string;
}

export function AdvisorOnboardingStartTrialStep({
  advisorId,
}: AdvisorOnboardingStartTrialStepProps) {
  const navigate = useNavigate();
  const {
    viewState,
    isPending,
    pendingPlanType,
    isCheckoutLoading,
    clientSecret,
    hasActiveSubscription,
    hasPendingInvitation,
    isInCheckoutFlow,
    isFetching,
    checkoutContent,
    trialDetailFormatted,
    handleSelectPlan,
    handleBackToPlans,
  } = useStartTrialStep({ advisorId });

  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="flex items-center gap-3">
          <Loader2 className="text-primary size-5 animate-spin" />
          <p className="text-foreground text-sm">Loading subscription information...</p>
        </div>
      </div>
    );
  }

  if (viewState === 'checkout') {
    return (
      <CheckoutLayout
        content={checkoutContent}
        trialDetailFormatted={trialDetailFormatted}
        onBack={handleBackToPlans}
        isLoading={isCheckoutLoading}
        clientSecret={clientSecret}
      />
    );
  }

  if ((hasActiveSubscription || hasPendingInvitation) && !isInCheckoutFlow) {
    return (
      <div className="flex flex-col items-center px-4 sm:px-6 md:px-8">
        <div className="md:-mt-42 -mt-24 w-full max-w-3xl space-y-6 sm:-mt-32">
          <div className="space-y-2 text-center sm:space-y-3">
            <h2 className="flex items-center justify-center gap-2 text-xl font-semibold text-gray-950 sm:text-2xl">
              Subscription Status
              {hasActiveSubscription && (
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-100 sm:size-7">
                  <Check className="size-4 text-green-700 sm:size-5" />
                </div>
              )}
            </h2>
            <p className="text-foreground mt-2 text-sm sm:text-base">
              {hasActiveSubscription
                ? 'You have an active subscription. View your subscription details below.'
                : 'You have a pending subscription invitation. Please accept or decline it below.'}
            </p>
          </div>
          <SubscriptionStatusContent
            advisorId={advisorId}
            variant="compact"
            hideManagementActions
            onInvitationAccepted={() => navigate(`/advisors/${advisorId}/onboarding/activate-seat`)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-5xl space-y-6 sm:space-y-10">
        <div className="space-y-2 text-center sm:space-y-3">
          <h2 className="text-xl font-semibold text-gray-950 sm:text-2xl">
            Create your first LTC plan in ~5 minutes
          </h2>
          <p className="text-foreground mt-2 text-sm sm:text-base">
            You're almost there. Choose how you'd like to get started.
          </p>
        </div>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="space-y-6">
            <div className="space-y-2 text-center md:text-left">
              <h3 className="text-lg font-semibold text-gray-950 sm:text-xl">
                What you'll get with Waterlily
              </h3>
              <p className="text-foreground text-sm sm:text-base">
                The most advanced AI-driven personalized LTC planning
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {FEATURES.map(feature => (
                <FeatureItem
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </div>

            <div className="text-foreground flex flex-wrap items-center justify-center gap-4 text-xs font-medium sm:gap-8 sm:text-sm">
              <div className="flex items-center gap-2">
                <Check className="text-primary size-4" />
                <span>Full access</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="text-primary size-4" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <Card className="border-primary relative overflow-hidden border-2 transition-all duration-200">
              <CardHeader className="gap-0">
                <Badge variant="default">
                  <Sparkles className="text-primary size-4" />
                  Recommended
                </Badge>
                <CardTitle className="mb-1 mt-4 text-xl font-bold sm:text-2xl">
                  Start your 14-day free trial
                </CardTitle>
                <CardDescription className="text-foreground text-sm sm:text-base">
                  Get access immediately for free. Cancel any time.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {TRIAL_BENEFITS.map(benefit => (
                    <li
                      key={benefit}
                      className="text-foreground flex items-center gap-3 text-sm"
                    >
                      <div className="grid size-5 shrink-0 place-items-center rounded-full bg-green-50">
                        <Check className="size-3.5 text-green-800" />
                      </div>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  disabled={isPending}
                  onClick={() => handleSelectPlan('trial')}
                  className="w-full"
                >
                  {pendingPlanType === 'trial' ? (
                    <>
                      <Loader2 className="animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Get started for free'
                  )}
                </Button>

                <p className="text-muted-foreground text-center text-xs">
                  We'll remind you 1 week before your trial ends. Cancel anytime.
                </p>
              </CardContent>
            </Card>

            <div className="relative flex items-center gap-2">
              <Separator className="flex-1" />
              <div className="text-foreground relative flex shrink-0 justify-center text-xs uppercase">
                Or choose a paid plan to skip the trial
              </div>
              <Separator className="flex-1" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              {PAID_PLANS.map(plan => (
                <Card
                  key={plan.id}
                  className="relative p-0"
                >
                  {plan.badge && (
                    <Badge
                      variant="default"
                      className="bg-primary text-primary-foreground -translate-1/2 absolute left-1/2 top-0 rounded-full"
                    >
                      {plan.badge}
                    </Badge>
                  )}
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-gray-950 sm:text-base">
                      {plan.title}
                    </h4>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-xl font-bold text-gray-950 sm:text-2xl">
                        {plan.price}
                      </span>
                      <span className="text-foreground text-xs sm:text-xs">/mo</span>
                    </div>
                    <p className="text-muted-foreground text-xs sm:text-xs">{plan.billingNote}</p>

                    <ul className="mt-2 space-y-1 sm:mt-3 sm:space-y-1.5">
                      {plan.benefits.map(benefit => (
                        <li
                          key={benefit}
                          className="text-foreground flex items-center gap-1.5 text-xs sm:gap-2 sm:text-xs"
                        >
                          <Check className="text-primary size-3 shrink-0" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={isPending}
                      onClick={() => handleSelectPlan(plan.id)}
                      className="mt-4 w-full"
                    >
                      {pendingPlanType === plan.id ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        `Choose ${plan.id}`
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
